// Thin fetch layer against the Drive BFF, used by Mail's Drive integration
// (attach from Drive, large files as Drive links, Save to Drive, and the
// Drive cards under received messages). Mail's access token already carries
// the Drive audience, so every call is a plain bearer request — no extra
// sign-in. Mirror types are kept separate from Drive's C# records, the same
// way mailApi.ts mirrors the Mail BFF.

const apiBase = (import.meta.env.VITE_DRIVE_BFF_BASE_URL ?? "").replace(/\/+$/, "");

/** Where people open Drive items. Links we put into outgoing mail always use
 *  this origin — `/open/<id>` works for anyone who has been granted access. */
export const DRIVE_WEB_URL = (import.meta.env.VITE_DRIVE_WEB_URL ?? "https://drive.tribex.in").replace(/\/+$/, "");

/** The Drive folder that large mail attachments are uploaded into. */
export const MAIL_ATTACHMENTS_FOLDER = "Mail Attachments";

export type DriveRole = "owner" | "editor" | "viewer";

export interface DriveItem {
    id: string;
    parentId: string | null;
    kind: "Folder" | "File";
    name: string;
    contentType: string | null;
    sizeBytes: number;
    createdAt: string;
    updatedAt: string;
    isFavorite?: boolean;
    lastAccessedAt?: string | null;
    /** Only set when the item belongs to someone else (absent = you own it). */
    ownerEmail?: string;
    role?: DriveRole;
    canDownload?: boolean;
}

export interface DriveFolderRef {
    id: string;
    parentId: string | null;
    name: string;
}

export interface DriveSharedItem {
    item: DriveItem;
    permissionId: string;
    sharedBy: string;
    sharedAt: string;
}

export type ShareRole = "viewer" | "editor";

export function isDriveConfigured(): boolean {
    return apiBase.length > 0;
}

export function driveOpenUrl(id: string): string {
    return `${DRIVE_WEB_URL}/open/${encodeURIComponent(id)}`;
}

export function driveFolderUrl(id: string | null): string {
    return id ? `${DRIVE_WEB_URL}/folder/${encodeURIComponent(id)}` : DRIVE_WEB_URL;
}

/** Owners and editors can share (Drive's GrantAsync rule). */
export function canShareItem(item: DriveItem): boolean {
    return !item.role || item.role === "owner" || item.role === "editor";
}

/** Items you own carry no canDownload flag — you can always download them. */
export function canDownloadItem(item: DriveItem): boolean {
    return item.canDownload !== false;
}

/** Drive answers errors as ProblemDetails {title, detail}; surface those. */
async function problemMessage(res: Response): Promise<string> {
    const text = await res.text().catch(() => "");
    try {
        const p = JSON.parse(text) as { title?: string; detail?: string };
        const msg = [p.title, p.detail].filter(Boolean).join(" — ");
        if (msg) return msg;
    } catch {
        // not JSON — fall through
    }
    return text.trim().slice(0, 200) || `${res.status} ${res.statusText}`;
}

async function driveFetch<T>(path: string, token: string | undefined, init: RequestInit = {}): Promise<T> {
    if (!apiBase) throw new Error("Drive isn't configured for this Mail deployment.");
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body && typeof init.body === "string") headers.set("Content-Type", "application/json");
    if (token) headers.set("Authorization", `Bearer ${token}`);
    const res = await fetch(`${apiBase}${path}`, { ...init, headers, credentials: "omit" });
    if (!res.ok) {
        const err = new Error(await problemMessage(res)) as Error & { status?: number };
        err.status = res.status;
        throw err;
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

/**
 * Multipart upload with progress. fetch() has no upload progress, so this
 * goes through XHR. Resolves with the created item (Drive auto-renames
 * "x (2).ext" on a clash); rejects with Drive's ProblemDetails message on
 * quota / virus / size failures.
 */
function uploadFile(
    file: File,
    parentId: string | null,
    token: string | undefined,
    onProgress?: (fraction: number) => void
): Promise<DriveItem> {
    return new Promise((resolve, reject) => {
        if (!apiBase) {
            reject(new Error("Drive isn't configured for this Mail deployment."));
            return;
        }
        const form = new FormData();
        form.append("file", file);
        if (parentId) form.append("parentId", parentId);
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${apiBase}/api/files`);
        xhr.setRequestHeader("Accept", "application/json");
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText) as DriveItem);
                } catch {
                    reject(new Error("Drive returned an unreadable response."));
                }
                return;
            }
            let msg = `${xhr.status} ${xhr.statusText}`;
            try {
                const p = JSON.parse(xhr.responseText) as { title?: string; detail?: string };
                msg = [p.title, p.detail].filter(Boolean).join(" — ") || msg;
            } catch {
                // keep status text
            }
            reject(new Error(msg));
        };
        xhr.onerror = () => reject(new Error("Network error while uploading to Drive."));
        xhr.onabort = () => reject(new Error("Upload cancelled."));
        xhr.send(form);
    });
}

async function downloadFile(item: DriveItem, token: string | undefined): Promise<File> {
    if (!apiBase) throw new Error("Drive isn't configured for this Mail deployment.");
    const res = await fetch(`${apiBase}/api/files/${encodeURIComponent(item.id)}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "omit"
    });
    if (!res.ok) throw new Error(await problemMessage(res));
    const blob = await res.blob();
    return new File([blob], item.name, { type: item.contentType || blob.type || "application/octet-stream" });
}

export const driveApi = {
    listChildren: (parentId: string | null, token: string | undefined) =>
        driveFetch<DriveItem[]>(
            parentId ? `/api/items?parentId=${encodeURIComponent(parentId)}` : "/api/items",
            token
        ),
    listFolders: (token: string | undefined) => driveFetch<DriveFolderRef[]>("/api/folders", token),
    getItem: (id: string, token: string | undefined) =>
        driveFetch<DriveItem>(`/api/items/${encodeURIComponent(id)}`, token),
    ancestors: (id: string, token: string | undefined) =>
        driveFetch<DriveFolderRef[]>(`/api/items/${encodeURIComponent(id)}/ancestors`, token),
    recent: (token: string | undefined, take = 50) => driveFetch<DriveItem[]>(`/api/recent?take=${take}`, token),
    search: (q: string, token: string | undefined, take = 50) =>
        driveFetch<DriveItem[]>(`/api/search?q=${encodeURIComponent(q)}&take=${take}`, token),
    sharedWithMe: (token: string | undefined) => driveFetch<DriveSharedItem[]>("/api/shared-with-me", token),
    createFolder: (name: string, parentId: string | null, token: string | undefined) =>
        driveFetch<DriveItem>("/api/folders", token, {
            method: "POST",
            body: JSON.stringify({ name, parentId })
        }),
    grant: (
        itemId: string,
        emails: string[],
        role: ShareRole,
        token: string | undefined,
        sourceRef = ""
    ) =>
        driveFetch<unknown>(`/api/items/${encodeURIComponent(itemId)}/permissions`, token, {
            method: "POST",
            body: JSON.stringify({
                emails,
                role,
                allowDownload: true,
                // Mailing a file never downgrades someone who was already an editor.
                keepHigherRole: true,
                source: "mail",
                sourceRef
            })
        }),
    uploadFile,
    downloadFile,

    /** Get-or-create the root "Mail Attachments" folder. */
    async mailAttachmentsFolder(token: string | undefined): Promise<DriveItem> {
        const root = await driveApi.listChildren(null, token);
        const existing = root.find((i) => i.kind === "Folder" && i.name === MAIL_ATTACHMENTS_FOLDER);
        if (existing) return existing;
        return driveApi.createFolder(MAIL_ATTACHMENTS_FOLDER, null, token);
    }
};

// ── Drive links inside message bodies ──────────────────────────────────────

export interface DriveLinkRef {
    id: string;
    kind: "open" | "folder";
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Origins whose /open/<id> and /folder/<id> links are ours. */
const DRIVE_ORIGINS = Array.from(new Set(["https://drive.tribex.in", DRIVE_WEB_URL]));
const DRIVE_LINK = new RegExp(
    `(?:${DRIVE_ORIGINS.map(escapeRegExp).join("|")})/(open|folder)/([0-9a-fA-F-]{32,36})`,
    "g"
);

/** Every distinct Drive item a message body links to, in order of appearance. */
export function findDriveLinks(html: string, max = 10): DriveLinkRef[] {
    const out: DriveLinkRef[] = [];
    const seen = new Set<string>();
    for (const m of html.matchAll(DRIVE_LINK)) {
        const id = (m[2] ?? "").toLowerCase();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push({ id, kind: m[1] === "folder" ? "folder" : "open" });
        if (out.length >= max) break;
    }
    return out;
}

// ── Link cards we insert into outgoing mail ────────────────────────────────

function esc(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

const BADGE_COLOURS: { exts: string[]; bg: string }[] = [
    { exts: ["pdf"], bg: "#d13438" },
    { exts: ["doc", "docx", "rtf", "odt", "txt"], bg: "#185abd" },
    { exts: ["xls", "xlsx", "csv", "ods"], bg: "#107c41" },
    { exts: ["ppt", "pptx", "odp"], bg: "#c43e1c" },
    { exts: ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "heic"], bg: "#8764b8" },
    { exts: ["zip", "rar", "7z", "gz", "tar"], bg: "#b7791f" },
    { exts: ["mp4", "mov", "avi", "mkv", "webm", "mp3", "wav"], bg: "#5c2e91" }
];

export function formatDriveBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/**
 * The link "chip" a shared Drive file becomes in the message body. Plain
 * inline-styled markup (no classes, scripts or tables) so it survives the
 * Syncfusion editor, the recipient's sanitiser and dark-mode mail clients.
 * `data-fg-drive` marks it so compose can tell which files to grant.
 */
export function driveLinkCardHtml(item: Pick<DriveItem, "id" | "name" | "sizeBytes" | "kind">): string {
    const ext = item.kind === "Folder" ? "" : (item.name.split(".").pop() ?? "").toLowerCase();
    const label = item.kind === "Folder" ? "DIR" : (ext && ext.length <= 4 ? ext.toUpperCase() : "FILE");
    const bg = BADGE_COLOURS.find((c) => c.exts.includes(ext))?.bg ?? "#616161";
    const url = item.kind === "Folder" ? driveFolderUrl(item.id) : driveOpenUrl(item.id);
    const size = item.kind === "Folder" ? "Drive folder" : `${formatDriveBytes(item.sizeBytes)} · Drive`;
    return (
        `<div data-fg-drive="${esc(item.id)}" style="margin:6px 0">` +
        `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer" ` +
        `style="display:inline-block;max-width:420px;padding:8px 12px;border:1px solid #d1d1d1;border-radius:6px;background:#ffffff;text-decoration:none;font-family:Segoe UI,Helvetica,Arial,sans-serif;font-size:13px;line-height:1.4;color:#242424">` +
        `<span style="display:inline-block;min-width:30px;padding:2px 4px;margin-right:8px;border-radius:3px;background:${bg};color:#ffffff;font-size:10px;font-weight:700;text-align:center;vertical-align:middle">${esc(label)}</span>` +
        `<span style="font-weight:600;color:#0f6cbd;vertical-align:middle">${esc(item.name)}</span>` +
        `<span style="display:block;margin-left:42px;color:#616161;font-size:11px">${esc(size)}</span>` +
        `</a></div>`
    );
}

/**
 * Splices `block` into a compose body just above the signature (or the quoted
 * reply history when there's no signature), so a link lands after what the
 * user typed rather than under the quote. Appends when neither is present.
 */
export function insertAboveSignature(bodyHtml: string, block: string): string {
    const candidates = [
        bodyHtml.indexOf('<div data-fg-signature="true"'),
        bodyHtml.search(/<hr\b[^>]*>\s*<p><strong>From:/i),
        bodyHtml.search(/<blockquote\b/i)
    ].filter((i) => i >= 0);
    if (candidates.length === 0) return bodyHtml + block;
    const at = Math.min(...candidates);
    return bodyHtml.slice(0, at) + block + bodyHtml.slice(at);
}
